import {
    Alert,
    Chip,
    Divider,
    Grid,
    IconButton,
    Paper,
    Snackbar,
    Stack,
    Tooltip,
    Typography,
    useTheme
} from "@mui/material"
import {styled} from '@mui/material/styles';
import {PoliceAvatar} from "../PoliceAvatar";
import {ProfileResponse} from "../../data/ProfileResponse";
import EditUserForm from "../../components/profile/EditUserForm";
import useUser from "../../hooks/useUser";
import React, {useCallback, useState} from "react";
import {LanguageOutlined, Policy, SettingsOutlined} from "@mui/icons-material";
import {Link, useNavigate} from "react-router-dom";
import {makeApiCall} from "../../hooks/CancellableApiCall";
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import {AlertType} from "../../data/AlertType";
import {VanityTagInformation} from "../../vanityTagConfig";

export interface ProfileInfoProps {
    data: ProfileResponse,
    inactivityNotice: boolean
}

export function removeNewlineSpam(data: string): string {
    let cleanedData = ""
    let usedNewLines = 0
    for (let i = 0; i < data.length; i++) {
        if (data[i] === "\n" && data[i + 1] === "\n") {
            continue
        }
        if (usedNewLines >= 15 && data[i] === "\n") {
            continue
        } else if (data[i] === "\n") {
            usedNewLines += 1
        }

        cleanedData += data[i]
    }
    return cleanedData
}

export const AvatarBlock = styled('div')`
        height: 150px;
        display: flex;
    `

export const TextBracket = styled('div')`
        margin-left: 25px;
        display: flex;
        height: 100%;
        overflow: auto;
        justify-content: center;
        flex-direction: column;
        align-content: center;
        span {
            color: "secondary";
            margin-bottom: 7px;
        }
    `

export function ProfileInfo(props: ProfileInfoProps) {
    const userData = useUser()
    const theme = useTheme()
    const navigate = useNavigate()
    const [showAlert, setShowAlert] = useState(false)
    const [alertType, setAlertType] = useState<AlertType>("success")
    const [alertText, setAlertText] = useState("")

    const setAlert = useCallback((type: AlertType, text: string) => {
        setAlertType(type)
        setAlertText(text)
        setShowAlert(true)
    }, [setAlertType, setAlertText, setShowAlert])

    let about;
    if (props.data.aboutMe && props.data.aboutMe.trim().length !== 0) {
        about = <Typography variant="body2"
                            style={{whiteSpace: "pre-wrap", marginTop: 4}}><em>{removeNewlineSpam(props.data.aboutMe)}</em></Typography>
    } else {
        about = <Typography variant="body2" sx={{mt:0.5}}>
            <em>
                {`${props.data.rpName} hasn't filled in their 'about me' section yet!`}
            </em>
        </Typography>
    }


    let blacklistedChip = <></>
    if ((props.data.blacklisted) && (userData.user?.permissions.includes("records:viewRecords") || (userData.user?.communityId === props.data.communityId))) {
        blacklistedChip = <Chip sx={{ml: 1}} label="Blacklisted" size="small" color={"error"}/>
    }

    let dischargedChip = <></>
    if ((props.data.discharged) && (userData.user?.permissions.includes("records:viewRecords") || (userData.user?.communityId === props.data.communityId))) {
        dischargedChip = <Chip sx={{ml: 1}} label="Discharged" size="small" color={"primary"}/>
    }

    let suspendedChip = <></>
    if ((props.data.suspended) && (userData.user?.permissions.includes("records:viewRecords") || (userData.user?.communityId === props.data.communityId))) {
        suspendedChip = <Chip sx={{ml: 1}} label="Suspended" size="small" color={"warning"}/>
    }

    let disabledChip = <></>
    if (!props.data.enabled) {
        disabledChip = <Chip sx={{ml: 1}} label="Account Disabled" size="small" color={"warning"}/>
    }

    let editProfileButton = <></>
    if (userData.user?.communityId === props.data.communityId) {
        editProfileButton = <IconButton
            size="small"
            color="info"
            component={Link}
            to={"/profile/edit"}
        >
            <SettingsOutlined/>
        </IconButton>
    }

    let viewRecordsButton = <></>
    if (userData.user?.permissions.includes("records:viewRecords") && userData.user.communityId !== props.data.communityId) {
        viewRecordsButton = <IconButton
            size="small"
            color="error"
            component={Link}
            to={`/record-search?target=${props.data.communityId}`}
        >
            <Policy/>
        </IconButton>
    }

    let syncAccountButton = <></>
    if (userData.user?.communityId === props.data.communityId) {
        syncAccountButton = <IconButton size="small" color="info" onClick={() => {
            makeApiCall({
                url: `/api/user/${props.data.communityId}/sync`,
                method: "PATCH",
                body: {},
                onLoadedCallback: () => {
                    navigate("/")
                },
                onError: () => {
                    setAlert("error", "You can only do this once a day")
                }
            })
        }}>
            <CloudSyncIcon/>
        </IconButton>
    }

    let managementRank = props.data.roles.find(T => {return T.divisionIdentifier === "server_management"})
    let vanityTagData = VanityTagInformation.find(T => {return T.rankIdentifiers.includes(managementRank?.identifier ?? "")})
    let vanityTag = <></>
    if (vanityTagData) {
        vanityTag = <>
            <Tooltip
                title = {vanityTagData.description}
                arrow>
                <Chip size="small" label={vanityTagData.displayName}
                      sx={{
                          backgroundColor: "#" + vanityTagData.backgroundColor,
                          color: vanityTagData.textColor,
                          padding:"1.7px",
                          display: {xs:"none", md:"block"}}}/>
            </Tooltip>
        </>
    }

    return <Grid container spacing={2}>
        <Grid item xs={12} md={12} lg={6} xl={4}>
            <Paper variant={"outlined"} sx={{padding: '15px'}}>
                <AvatarBlock>
                    <PoliceAvatar
                        online={props.data.online}
                        avatarUrl={props.data.fullAvatarLink}
                        color={vanityTagData ? vanityTagData.backgroundColor : props.data.rank?.color ?? "white"}
                        userCommunityId={props.data.communityId}
                        dimensions={150}
                    ></PoliceAvatar>
                    <TextBracket>
                        <Typography variant="h5" color="text.primary">{props.data.nick}</Typography>
                        <span><i className={`rank timeline-rank ${props.data.rank?.iconName}`}/> {props.data.rank?.displayName}</span>
                        <div>
                            <Tooltip title="Forum Profile">
                                <IconButton component={Link}
                                            to={`https://api.perpheads.com/v2/profile/${props.data.communityId}`}
                                            size="small" color="info"><LanguageOutlined/></IconButton>
                            </Tooltip>
                            <Tooltip title="Edit Profile">
                                {editProfileButton}
                            </Tooltip>
                            <Tooltip title="Records">
                                {viewRecordsButton}
                            </Tooltip>
                            <Tooltip title="Sync Steam Profile (This will only update your PFP and Nick to be inline with your steam data, this will not re-sync server details)">
                                {syncAccountButton}
                            </Tooltip>
                            {userData.user?.permissions.includes("admin:user") ?
                                <EditUserForm userApiResponse={userData} user={props.data}/> : <></>
                            }
                        </div>
                    </TextBracket>
                </AvatarBlock>
            </Paper>
        </Grid>
        <Grid item xs={12} md={12} lg={6} xl={8}>
            <Paper variant={"outlined"}
                   sx={{padding: "15px", height: "181px", overflow: "auto", wordWrap: "break-word"}}>
                <Typography variant="h6" gutterBottom component="span">
                    About Me:
                    <Stack direction={"row"} sx={{float: "right"}} spacing={0.5}>
                        {vanityTag}
                        {disabledChip}
                        {blacklistedChip}
                        {dischargedChip}
                        {suspendedChip}
                        {props.data.inactivityNotice ? <Chip label="Inactivity Notice" size="small" color={"info"}/> : <></>}
                    </Stack>
                </Typography>
                <Divider/>
                <Typography component="span" variant="body1" sx={{pt: 1}} color={theme.palette.text.secondary}>
                    {about}
                </Typography>
            </Paper>
        </Grid>
        <Snackbar open={showAlert} autoHideDuration={5000} onClose={() => setShowAlert(false)}>
            <Alert severity={alertType} onClose={() => setShowAlert(false)} sx={{width: "100%"}} variant="filled">
                {alertText}
            </Alert>
        </Snackbar>
    </Grid>
}
